import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { EPCR } from '@/types';
import { activeSets, type ConditionalSet } from '@/lib/conditional-cascade';

/**
 * CascadeRemovalPrompt — EMSPCR Foundation v1.0 §3.4.
 *
 * Watches the active conditional sets. When a set that was active
 * drops (e.g. the crew changes primary impression away from a trauma
 * term), this component asks once:
 *
 *   "Trauma section no longer required. Keep entered data or clear?"
 *   [ Keep ] [ Clear ] [ Cancel ]
 *
 * Default on dismiss = Keep (prevents accidental data loss). Clear
 * routes through onClear with a clear_set_fields() projection;
 * archived data remains in version history elsewhere.
 *
 * One prompt per dropped set. If the crew reactivates the set inside
 * the session we re-arm the watcher so a second drop will prompt
 * again.
 */

interface Props {
  epcr: EPCR;
  onClear: (setId: string, clearPatch: Partial<EPCR>) => void;
}

// Fields to zero out when a given set is cleared. Only includes
// set-scoped data — does NOT touch shared fields (primary impression,
// disposition, etc.) that may be the cause of the set drop.
const CLEAR_PATCH: Record<string, Partial<EPCR>> = {
  trauma: {
    possible_injury: null,
    injury_causes: [],
    trauma_triage_red: [],
    trauma_triage_yellow: [],
    alcohol_drug_indicators: [],
    mvc_vehicle_type: null,
    mvc_patient_role: null,
    mvc_seat_position: null,
    mvc_restraints: [],
    mvc_airbags: [],
    mvc_high_risk_indicators: [],
    mvc_extrication_required: null,
    mvc_extrication_start: null,
    mvc_extrication_end: null,
    mvc_estimated_speed_mph: null,
    mvc_speed_limit_mph: null,
    mvc_rollover: null,
    mvc_other_fatalities: null,
    trauma_fall_height_ft: null,
    trauma_assault_weapon_type: null,
    trauma_burn_tbsa_pct: null,
    trauma_burn_depth: null,
    trauma_burn_cause: null,
    trauma_penetrating_mechanism: null,
    peds_weight_kg: null,
    peds_broselow_color: null,
    peds_caregiver_present: null,
    peds_car_seat_appropriate: null,
    peds_suspected_nat: null,
    peds_dcyf_reporter_name: null,
    peds_dcyf_report_time: null,
  },
  refusal: {
    // refusal-specific data lives mostly in signatures + transport_decision.
  },
  overdose: {
    alcohol_drug_indicators: [],
  },
};

export function CascadeRemovalPrompt({ epcr, onClear }: Props) {
  const previousRef = useRef<Set<string>>(new Set());
  const [pending, setPending] = useState<ConditionalSet | null>(null);
  const dismissedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const current = new Set<string>(
      activeSets(epcr).map((s) => s.id as string).filter((id) => id !== 'base'),
    );
    // Detect drops: was in previous, not in current
    let droppedId: string | null = null;
    for (const id of previousRef.current) {
      if (!current.has(id)) { droppedId = id; break; }
    }
    // Reset dismissal tracking when a set comes back
    for (const id of current) {
      if (!previousRef.current.has(id)) dismissedRef.current.delete(id);
    }
    if (droppedId && !dismissedRef.current.has(droppedId) && !pending) {
      // Find the dropped set definition for labeling
      const lookup = activeSets({ ...epcr, primary_impression: 'trauma' }); // retrieve set metadata
      void lookup;
      // Import dynamically to avoid circular deps at module load
      import('@moquintrend/compliance-rules').then(({ CONDITIONAL_SETS }) => {
        const def = (CONDITIONAL_SETS as Record<string, typeof CONDITIONAL_SETS[keyof typeof CONDITIONAL_SETS]>)[droppedId as string];
        if (def) {
          const { test: _test, ...pub } = def;
          void _test;
          setPending(pub as ConditionalSet);
        }
      });
    }
    previousRef.current = current;
    // Intentionally omit `pending` so the effect tracks epcr changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epcr]);

  if (!pending) return null;

  const label = pending.name;
  const rationale = pending.rationale;

  function keep() {
    dismissedRef.current.add(pending!.id);
    setPending(null);
  }
  function clear() {
    const patch = CLEAR_PATCH[pending!.id] ?? {};
    onClear(pending!.id, patch);
    dismissedRef.current.add(pending!.id);
    setPending(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md border border-amber-700 bg-slate-950 shadow-2xl">
        <header className="flex items-center justify-between border-b border-amber-800 bg-amber-950/40 px-3 py-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-300" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-amber-200">
              Conditional set dropped
            </span>
          </div>
          <button onClick={keep} className="p-1 text-slate-500 hover:text-slate-200" title="Close (keep data)">
            <X size={12} />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">
              {label} is no longer required.
            </div>
            <p className="mt-1 text-[12px] leading-snug text-slate-400">
              The data you entered in that section is still saved. Choose whether to keep
              it on this report or clear it and start fresh.
            </p>
          </div>
          <div className="rounded border border-slate-800 bg-slate-900/60 p-2 text-[11px] leading-snug text-slate-400">
            <span className="font-bold uppercase tracking-widest text-[9px] text-slate-500 mr-1">
              Why this was active
            </span>
            {rationale}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={keep} className="btn-primary text-sm gap-1">
              Keep
            </button>
            <button onClick={clear} className="btn-secondary text-sm gap-1">
              Clear
            </button>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 text-right">
            Default on dismiss: keep · prevents accidental data loss
          </p>
        </div>
      </div>
    </div>
  );
}
